// Cálculo do preço efetivo de assinatura com desconto manual (story 3.8).
// DR-024 — desconto manual aplicado pela financeira.
//
// Versão `number` pra preview UI. Backend (`charge-generation.service.ts`)
// mantém versão `Prisma.Decimal` pra precisão monetária na cobrança real.
// Mesma fórmula em ambos — tests garantem consistência.

import { todayLocal } from './datetime';

export type DiscountType = 'percent' | 'fixed';

export interface DiscountInput {
  /** Preço base da assinatura (snapshot do plano). */
  price: number;
  /** Valor do desconto: `>0`. % se type=percent, R$ se type=fixed. */
  discountAmount: number | null;
  /** Tipo do desconto. */
  discountType: DiscountType | null;
  /** Validade do desconto YYYY-MM-DD. Null = perpétuo. */
  discountUntil: string | null;
  /** Teto em R$ do desconto percentual (vem do cupom). Null/ausente = sem teto. */
  discountMax?: number | null;
}

/**
 * Aplica desconto vigente e retorna preço efetivo arredondado em 2 casas.
 *
 * Regras (DR-024 + alinhada com charge-generation.service.ts):
 * - amount/type ambos null/undefined → sem desconto, retorna `price`
 * - `discountUntil < today` → expirado, retorna `price` (reversão automática)
 * - `discountUntil` null OU `>= today` → aplica desconto
 * - `percent`: `max(0, price × (1 − amount/100))` — clampa a zero
 * - `fixed`: `max(0, price − amount)` — clampa a zero
 */
export function calculateEffectivePrice(input: DiscountInput): number {
  const { price, discountAmount, discountType, discountUntil, discountMax } = input;
  if (discountAmount === null || discountAmount === undefined) return round2(price);
  if (!discountType) return round2(price);
  if (discountUntil && discountUntil < todayLocal()) return round2(price);

  if (discountType === 'percent' && discountMax !== null && discountMax !== undefined) {
    // Teto do cupom: o abatimento em R$ nunca passa de discountMax — mesma
    // regra de calculateCouponDiscount (1ª fatura), pra renovação não dar mais.
    const discount = Math.min(price * (discountAmount / 100), Math.max(0, discountMax));
    return round2(Math.max(0, price - discount));
  }
  if (discountType === 'percent') {
    const factor = Math.max(0, 1 - discountAmount / 100);
    return round2(Math.max(0, price * factor));
  }
  // fixed
  return round2(Math.max(0, price - discountAmount));
}

/**
 * Retorna o valor monetário do desconto (price - effectivePrice).
 * Útil pra UI exibir "Desconto: -R$ X".
 */
export function calculateDiscountAmount(input: DiscountInput): number {
  const effective = calculateEffectivePrice(input);
  return round2(Math.max(0, input.price - effective));
}

// =============================================================================
// CUPONS DE DESCONTO — Checkout (spec: docs/specs/checkout-coupons.spec.md)
// Reusa a MESMA fórmula percent/fixed acima; adiciona teto (maxDiscount) e o
// floor mínimo do Asaas. Fonte única da matemática de desconto do checkout.
// =============================================================================

/**
 * Valor mínimo cobrável no Asaas (PIX/boleto). Um cupom NÃO pode deixar o preço
 * final abaixo disso — senão a cobrança é rejeitada. Usado na elegibilidade.
 */
export const COUPON_MIN_CHARGE = 5;

export interface CouponDiscountInput {
  /** Preço base (cheio) do ciclo escolhido. */
  price: number;
  /** Tipo do desconto. */
  discountType: DiscountType;
  /** Valor do desconto `> 0`: % se percent, R$ se fixed. */
  discountValue: number;
  /** Teto do desconto em R$ (só faz sentido em percent). Null/undefined = sem teto. */
  maxDiscount?: number | null;
}

export interface CouponDiscountResult {
  /** Valor abatido, em R$, arredondado 2 casas. */
  discountAmount: number;
  /** Preço a cobrar após o desconto, arredondado 2 casas. */
  finalPrice: number;
}

/**
 * Calcula o desconto de um cupom sobre o preço base.
 * - `percent`: desconto = price × value/100, limitado por `maxDiscount` (se houver)
 * - `fixed`: desconto = value (em R$)
 *
 * Clampa o desconto a `[0, price]` (nunca abate mais que o preço) e arredonda 2 casas.
 * NÃO decide elegibilidade do floor — use `isAboveAsaasFloor(finalPrice)` pra isso.
 */
export function calculateCouponDiscount(input: CouponDiscountInput): CouponDiscountResult {
  const { price, discountType, discountValue, maxDiscount } = input;
  const base = Math.max(0, price);
  const value = Math.max(0, discountValue);

  let discount: number;
  if (discountType === 'percent') {
    discount = base * (value / 100);
    if (maxDiscount !== null && maxDiscount !== undefined) {
      discount = Math.min(discount, Math.max(0, maxDiscount));
    }
  } else {
    discount = value;
  }

  // Desconto nunca maior que o próprio preço (clampa a zero o resultado).
  discount = Math.min(round2(discount), base);
  const finalPrice = round2(Math.max(0, base - discount));
  return { discountAmount: round2(discount), finalPrice };
}

/** True se o preço final é cobrável no Asaas (>= mínimo). */
export function isAboveAsaasFloor(finalPrice: number): boolean {
  return round2(finalPrice) >= COUPON_MIN_CHARGE;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
